/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, all, select, call, takeLatest, race, take } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  ActionRequest,
  ActionSuccess,
  ActionFail,
  XError,
  MenuItem,
  Menu,
  MenuUiArea,
  MenuErrors,
  FileFormatToFileTypeMap,
  Constants,
  AssetsApi,
  MenuItemType,
  Channel,
  ParentalControlSettings,
  HideUnsubscribed,
  Ducks,
  Asset,
} from '@wit/vtv-x-foundation-lib';
import { Selectors as GdprSelectors } from 'core/store/ducks/gdpr';
import { StoreState } from 'store/store';
import { Selectors as ChannelsSelectors } from './channels';
import AppConfig from '../../../../services/AppConfig';

export enum ActionTypes {
  REQUEST = 'topChannels/REQUEST',
  SUCCESS = 'topChannels/SUCCESS',
  FAIL = 'topChannels/FAIL',
}

type LoadTopChannels = ActionRequest<ActionTypes.REQUEST>;
type LoadTopChannelsSuccess = ActionSuccess<ActionTypes.SUCCESS, Channel[]>;
type LoadTopChannelsFail = ActionFail<ActionTypes.FAIL>;

type Actions = LoadTopChannels | LoadTopChannelsSuccess | LoadTopChannelsFail;

const Actions = {
  request: (): LoadTopChannels => ({ type: ActionTypes.REQUEST }),
  success: (payload: Channel[]): LoadTopChannelsSuccess => ({
    type: ActionTypes.SUCCESS,
    payload,
  }),
  fail: (error: XError): LoadTopChannelsFail => ({ type: ActionTypes.FAIL, error }),
};

export const Creators = {
  loadTopChannels: (): LoadTopChannels => Actions.request(),
};

export interface ReducerState {
  processing?: boolean;
  processed?: boolean;
  error?: XError;
  data?: Channel[];
}

const initialState: ReducerState = {};

export default function topChannelsReducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionTypes.FAIL:
      return {
        ...state,
        processing: false,
        processed: true,
        error: action.error,
      };
    case ActionTypes.REQUEST:
      return {
        ...state,
        processing: true,
        processed: false,
      };
    case ActionTypes.SUCCESS: {
      return {
        ...state,
        data: action.payload,
        processing: false,
        processed: true,
      };
    }
    default:
      return state;
  }
}

// SELECTORS
const PAGINATED_TOP_CHANNELS_NAMESPACE = 'TOP_CHANNELS';

export const Selectors = {
  isProcessed: (state: StoreState): boolean => state.topCannels.processed === true,
  getError: (state: StoreState): XError | undefined => state.topCannels.error,
  getTopChannels: (state: StoreState): Channel[] | undefined => {
    return state.topCannels?.data;
  },
};

// SAGAS

function* getTopChannelsFromMenu(): SagaIterator<MenuItem | undefined> {
  const menuAlreadyCalled = yield select(Ducks.Menu.Selectors.isProcessed);
  if (menuAlreadyCalled) {
    const menu: Menu = yield select(Ducks.Menu.Selectors.getMenu);
    if (menu) {
      const channelsMenuItem = menu[MenuUiArea.EPG];
      if (channelsMenuItem) {
        const {
          children: [topChannel],
        }: MenuItem | undefined = channelsMenuItem;
        if (topChannel) {
          return topChannel;
        }
        return undefined;
      }
      throw XError.fromCode(MenuErrors.validation.missingMenuItemTvGuide);
    } else {
      throw new Error('Channels saga: Menu is undefined');
    }
  } else {
    throw new Error('Channels saga called before Menu');
  }
}

function* getTopChannelsFromApi(channelId: number): SagaIterator {
  yield Ducks.Pagination.clearPaginatedApi(PAGINATED_TOP_CHANNELS_NAMESPACE);
  const filesFormat: FileFormatToFileTypeMap = yield select(Ducks.Dms.Selectors.getFilesFormat);
  const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);

  const result: Channel[] = yield Ducks.Pagination.callPaginatedApi(
    PAGINATED_TOP_CHANNELS_NAMESPACE,
    0,
    Constants.TOP_CHANNELS_API_PAGE_SIZE,
    Constants.TOP_CHANNELS_API_PAGE_SIZE,
    AssetsApi.assetList,
    channelId,
    filesFormat,
    parentalControlSettings,
    HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY]
  );
  return result;
}

function* getTopChannelsRecomm(topChannelsItem: MenuItem): SagaIterator {
  const hasAgreedToConsent: boolean = yield select(GdprSelectors.hasAgreedToConsent);
  const isRecommendationsEnabled: MenuItem | undefined = yield select(Ducks.Recommendations.Selectors.isFeatureEnabled);

  if (!isRecommendationsEnabled || !hasAgreedToConsent) {
    return [];
  }

  const recommContext = {
    menuUiArea: MenuUiArea.EPG,
    menuItemId: topChannelsItem.id,
  };

  yield put(Ducks.Recommendations.Creators.loadAssets(recommContext));

  const [success] = yield race([
    Ducks.Recommendations.takeAssets(Ducks.Recommendations.ActionType.SUCCESS, recommContext),
    Ducks.Recommendations.takeAssets(Ducks.Recommendations.ActionType.FAIL, recommContext),
  ]);

  if (success) {
    const topChannelsRecommAssets: Asset[] = yield select(Ducks.Recommendations.Selectors.assets, recommContext);
    return topChannelsRecommAssets as Channel[];
  }

  return [];
}

function* checkTopChannelsIsValid(): SagaIterator<boolean> {
  const topChannels: Channel[] = yield select(Selectors.getTopChannels);
  const channels: Channel[] = yield select(ChannelsSelectors.getChannels);

  const invalidTopChannel = topChannels.find((topChannel) => {
    const foundChannel = channels?.find((channel) => channel.epgId === topChannel.epgId);
    if (foundChannel) {
      return false;
    }
    return true;
  });

  if (invalidTopChannel) {
    return false;
  }
  return true;
}

export function* getTopChannels(): SagaIterator<Channel[] | undefined> {
  let topChannels: Channel[] | undefined;
  topChannels = yield select(Selectors.getTopChannels);
  let isValid = false;

  if (topChannels) {
    // check if the top channels are valid
    isValid = yield call(checkTopChannelsIsValid);
  }

  if (!isValid) {
    yield put(Creators.loadTopChannels());
    const { success } = yield race({
      success: take(ActionTypes.SUCCESS),
      error: take(ActionTypes.FAIL),
    });
    if (success) {
      topChannels = yield select(Selectors.getTopChannels);
    }
  }

  return topChannels;
}

function* filterValidTopChannels(topChannels: Channel[]): SagaIterator<Channel[]> {
  const channels: Channel[] = yield select(ChannelsSelectors.getChannels);
  let validChannels: Channel[] = [];

  validChannels = topChannels.filter((topChannel) => {
    return channels?.find((channel) => channel.epgId === topChannel.epgId) !== undefined;
  });

  return validChannels;
}

function* loadTopChannelsSaga(): SagaIterator {
  try {
    let channels: Channel[] = [];
    const topChannels: MenuItem | undefined = yield call(getTopChannelsFromMenu);
    switch (topChannels?.type) {
      case MenuItemType.EXTERNAL:
        channels = yield call(getTopChannelsRecomm, topChannels);
        break;
      case MenuItemType.KALTURA:
        if (topChannels.channelId) {
          channels = yield call(getTopChannelsFromApi, topChannels.channelId);
        }
        break;
      default:
        break;
    }
    channels = yield call(filterValidTopChannels, channels);
    yield put(Actions.success(channels));
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error)));
  }
}

export function* saga(): SagaIterator {
  yield all([takeLatest(ActionTypes.REQUEST, loadTopChannelsSaga)]);
}
